import {
  Accordion,
  AccordionDetails,
  Grid,
  Typography,
  useEventCallback,
  IconButton,
} from '@mui/material';
import { memo, useMemo, useState } from 'react';
import { ExpandMore } from '@mui/icons-material';
import { PmiButton } from 'shell/components/shared/button';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { FeatureVM } from 'shell/view-models/feature';
import Table from 'shell/components/shared/table';
import { FEATURE_AUTHORIZATION_ANPR, head } from './constants';
import { AuthorizationForm, AutorizationVM } from './types';
import FeatureAuthList from 'system/components/shared/feature-auth-list';
import FeatureAuthValueList from 'system/components/shared/feature-auth-value-list';
import { noop } from 'shell/utils/noop';
import Delete from '@mui/icons-material/Delete';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { OperatorForm } from 'system/view-models/operator';
import { AuthorizationsList } from 'shell/models/feature';
import OperatorList from 'front-office/components/shared/operator-list';
import { ItemVM } from 'shell/view-models/item';
import { Operator as OperatorM, AutorizzazioniOperatore } from 'shell/models/operator';
import { HttpResponseList } from 'shell/models/http-response';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { Operation } from 'shell/view-models/operation';

const validationSchema = yup.object().shape({
  type: yup.object().shape({
    id: yup.string().required('Campo obbligatorio'),
    value: yup.string().required('Campo obbligatorio'),
  }),
  value: yup.object().shape({
    id: yup.string().required('Campo obbligatorio'),
    value: yup.string().required('Campo obbligatorio'),
  }),
});

const initialValues = {
  page: 0,
  rowsPerPage: 10,
  type: { id: '', value: '' },
  value: { id: '', value: '' },
};

interface AuthorizationFlagsProps {
  title: string;
  disabled?: boolean;
  fiscalCode?: string;
  typeOperator?: string;
  features?: FeatureVM;
  handleChange: (key: keyof FeatureVM, value: FeatureVM[keyof FeatureVM]) => void;
  handleChanges: (values: OperatorForm) => void;
}

const AuthorizationFlags = ({
  title,
  features,
  fiscalCode,
  handleChange,
  disabled,
  handleChanges,
}: AuthorizationFlagsProps): React.ReactElement => {
  const body: Array<AutorizationVM> = useMemo(() => {
    const newBody = features?.authorizationsList?.slice() || [];
    return newBody
      .map((item) => {
        return {
          ...item,
          action: (
            <IconButton color="cdmBrand" disabled={disabled} onClick={() => onDelete(item.id)}>
              <Delete />
            </IconButton>
          ),
        };
      })
      .sort((a: AutorizationVM, b: AutorizationVM) => {
        const aElement = a || {};

        const bElement = b || {};

        if (aElement?.type < bElement?.type) {
          return -1;
        }

        return 0;
      });
  }, [features?.authorizationsList]);

  const addNewAutorization = useEventCallback(() => {
    let newBody = features?.authorizationsList?.slice() || [];

    const alreadyPresent = features?.authorizationsList?.find(
      (item) =>
        item.id === formContext.values.type?.id && item.value === formContext.values.type?.value
    );

    if (alreadyPresent) {
      formContext.setValues(initialValues);
      return;
    }

    // eliminare oggetto se già esistente ma con valore diverso
    newBody = newBody.filter((item) => item.id !== formContext.values.type?.id);

    const firstElement: Array<AutorizationVM> = [
      {
        id: formContext.values.type?.id || '',
        type: formContext.values.type?.value || '',
        value: formContext.values.value?.value || '',
      },
    ];

    // reset filtri di ricerca
    formContext.setValues(initialValues);

    if (
      Number.parseInt(firstElement[0].id || '') === FEATURE_AUTHORIZATION_ANPR &&
      firstElement[0].value?.split(' ')[0] === '0'
    ) {
      // dati ANPR non obbligatori e resettare accordion
      handleChanges({
        anpr: {
          isEditAnprData: false,
          isRequiredAnpr: false,
          anprOperatorCode: undefined,
          validityStart: null,
          validityEnd: null,
          stationIdAnpr: undefined,
        },
        feature: {
          authorizationsList: firstElement.concat(newBody) as AuthorizationsList[],
        },
      });

      return;
    }

    if (
      Number.parseInt(firstElement[0].id || '') === FEATURE_AUTHORIZATION_ANPR &&
      (firstElement[0].value?.split(' ')[0] === '1' || firstElement[0].value?.split(' ')[0] === '2')
    ) {
      // dati ANPR obbligatori
      handleChanges({
        anpr: {
          isEditAnprData: true,
          isRequiredAnpr: true,
          anprOperatorCode: fiscalCode || undefined,
        },
        feature: {
          authorizationsList: firstElement.concat(newBody) as AuthorizationsList[],
        },
      });

      return;
    }

    // aggiunta se autorizzazione è diversa da 29
    handleChange('authorizationsList', firstElement.concat(newBody));
  });

  const formContext = useFormik<AuthorizationForm>({
    validationSchema,
    initialValues,
    validateOnMount: true,
    onSubmit: addNewAutorization,
  });
  const [open, setOpen] = useState<boolean>(false);
  const [operatorSearch, setOperatorSearch] = useState<(ItemVM & { operator?: OperatorM }) | null>(
    null
  );

  const getAutorizzazioniOperatore = useHttpClient<
    HttpResponseList<AutorizzazioniOperatore>,
    AutorizzazioniOperatore[]
  >(`${__MANAGEMENT_API_URL__}/operatori/autorizzazioni-operatore`, {
    transformer: (response) => response.data.payload.list,
  });

  const handleCopyAuthorizations = useEventCallback(() => {
    const codiceOperatore = operatorSearch?.operator?.key?.codiceOperatore;

    if (!codiceOperatore) return;

    (
      getAutorizzazioniOperatore({
        params: {},
        paramsSerializer: () => `filters=userName:${codiceOperatore}:${Operation.EQUALS}`,
      }) as ResponsePromise<HttpResponseList<AutorizzazioniOperatore>, AutorizzazioniOperatore[]>
    ).then((response) => {
      const risultato = (response as AutorizzazioniOperatore[])?.[0];
      const authList: AutorizationVM[] = [];

      risultato?.autorizzazioni?.forEach((autorizzazione) => {
        autorizzazione?.featureValoreList?.forEach((valore) => {
          valore?.autorizzazioneOperatore?.forEach((authOpe) => {
            authList.push({
              id: String(authOpe?.key?.codiceFeature || ''),
              type: authOpe?.descrizioneAutorizzazione || '',
              value: `${authOpe?.valoreFeatureAuthOpe} - ${authOpe?.descrizioneValoreFeatureAuthOpe}`,
            });
          });
        });
      });

      // Gestione ANPR: verifico se tra le autorizzazioni copiate c'è la feature 29
      const anprAuth = authList.find(
        (item) => Number.parseInt(item.id || '') === FEATURE_AUTHORIZATION_ANPR
      );

      if (anprAuth) {
        const anprValue = anprAuth.value?.split(' ')[0];

        if (anprValue === '1' || anprValue === '2') {
          // ANPR autorizzato → aggiorno solo i flag, mantenendo i dati ANPR già compilati
          handleChanges({
            anpr: {
              isEditAnprData: true,
              isRequiredAnpr: true,
              anprOperatorCode: fiscalCode || undefined,
            },
            feature: {
              authorizationsList: authList as AuthorizationsList[],
            },
          });
        } else {
          // ANPR non autorizzato → resetto i campi ANPR
          handleChanges({
            anpr: {
              isEditAnprData: false,
              isRequiredAnpr: false,
              anprOperatorCode: undefined,
              validityStart: null,
              validityEnd: null,
              stationIdAnpr: undefined,
            },
            feature: {
              authorizationsList: authList as AuthorizationsList[],
            },
          });
        }
      } else {
        handleChange('authorizationsList', authList);
      }

      setOperatorSearch(null);
    });
  });

  const [tableProps, setTableProps] = useState({ page: 0, rowsPerPage: 10 });

  const handleFeatureAuthListChange = useEventCallback((id: string, value: string) => {
    formContext.setFieldValue('type', { id: id || '', value: value || '' });
  });

  const handleFeatureAuthValueListChange = useEventCallback((id: string, value: string) => {
    formContext.setFieldValue('value', { id: id || '', value: value || '' });
  });

  const onDelete = useEventCallback((id: string | undefined) => {
    if (Number.parseInt(id || '') === FEATURE_AUTHORIZATION_ANPR) {
      // dati ANPR non obbligatori e resettare accordion
      handleChanges({
        anpr: {
          isEditAnprData: false,
          isRequiredAnpr: false,
          anprOperatorCode: undefined,
          validityStart: null,
          validityEnd: null,
          stationIdAnpr: undefined,
        },
        feature: {
          authorizationsList: (features?.authorizationsList?.filter((el) => el.id !== id) ||
            []) as AuthorizationsList[],
        },
        operator: {
          typeOperator: '',
        },
      });

      return;
    }

    handleChange(
      'authorizationsList',
      features?.authorizationsList?.filter((el) => el.id !== id) || []
    );
  });

  const onChangePage = useEventCallback((page: number) => {
    setTableProps({ ...tableProps, page: page || 0 });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    setTableProps({ ...tableProps, rowsPerPage: rowsPerPage || 0 });
  });

  return (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-operators-content"
        id="panel-operators-header"
        classes={{
          root: 'pmi-flex-row-reverse',
        }}
        expandIcon={<ExpandMore />}
      >
        <Typography>{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div className="pmi-mt-2">
          <Grid item container spacing={2}>
            {disabled ? undefined : (
              <Grid item container spacing={2} xs={12} alignItems="center">
                <Grid item xs={12} md={6}>
                  <OperatorList
                    onChange={(_, operator) =>
                      setOperatorSearch(operator as (ItemVM & { operator?: OperatorM }) | null)
                    }
                    label="Copia autorizzazioni da operatore"
                    value={operatorSearch?.id?.toString() || ''}
                    disabled={disabled}
                    allOperator={false}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <PmiButton
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={handleCopyAuthorizations}
                    disabled={!operatorSearch?.id}
                  >
                    COPIA DA OPERATORE
                  </PmiButton>
                </Grid>
              </Grid>
            )}
            {disabled ? undefined : (
              <>
                <Grid item container spacing={2} xs={12}>
                  <Grid item xs={12} md={6}>
                    <FeatureAuthList
                      value={formContext.values.type?.id || ''}
                      onChange={handleFeatureAuthListChange}
                      selectedItems={features?.authorizationsList}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FeatureAuthValueList
                      value={formContext.values.value?.id || ''}
                      onChange={handleFeatureAuthValueListChange}
                      disabled={!formContext.values.type?.id}
                      idFeatureAuth={formContext.values.type?.id?.toString() || ''}
                    />
                  </Grid>
                </Grid>
                <Grid item container xs={12} justifyContent="flex-end">
                  <Grid item xs={12} md={4}>
                    <PmiButton
                      onClick={() => addNewAutorization()}
                      fullWidth
                      disabled={!formContext.isValid}
                      color="primary"
                      variant="contained"
                    >
                      Inserisci autorizzazione
                    </PmiButton>
                  </Grid>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <Table
                ariaLabel="Lista autorizzazioni"
                activeActions={{}}
                page={tableProps.page || 0}
                rowsPerPage={tableProps.rowsPerPage || 10}
                body={(body as Array<AutorizationVM>) || []}
                count={((features?.authorizationsList as Array<AutorizationVM>) || []).length}
                head={head}
                onSort={noop}
                onPageChange={onChangePage}
                onRowsPerPageChange={onChangeRowsPerPage}
                isNotLayoutFixed
                noSort
                hideButtons
                orders={{}}
                minWidth={800}
              />
            </Grid>
          </Grid>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

AuthorizationFlags.displayName = 'Form Authorization flags component';

const MemoizedAuthorizationFlags = memo(AuthorizationFlags);

export default MemoizedAuthorizationFlags as typeof AuthorizationFlags;
